/**
 * PurgeEmailButton — master-admin utility to fully wipe an email from every
 * MongoDB collection so it becomes re-registerable.
 *
 * Two-phase flow:
 *   1. CHECK — GET /api/master/purge-email/check?email=... shows exactly
 *      which collections still hold the email and whether signup is
 *      currently blocked. Renders as a summary card inside the dialog.
 *   2. PURGE — POST /api/master/purge-email {email} hard-deletes across
 *      users, blocked_credentials, deleted_users, builder_sites,
 *      wb_products, storefront_orders, etc.
 *
 * The dialog is intentionally verbose about what will be deleted because
 * this action is irreversible.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Trash2, Search, Loader2, Check, AlertTriangle, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authH = () => ({ Authorization: `Bearer ${localStorage.getItem('masterToken') || localStorage.getItem('token')}` });

export default function PurgeEmailButton() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [checking, setChecking] = useState(false);
  const [purging, setPurging] = useState(false);
  const [checkReport, setCheckReport] = useState(null);
  const [purgeReport, setPurgeReport] = useState(null);

  const reset = () => {
    setEmail('');
    setCheckReport(null);
    setPurgeReport(null);
  };

  const runCheck = async () => {
    const em = email.trim();
    if (!em || !em.includes('@')) {
      toast.error('Enter a valid email');
      return;
    }
    setChecking(true);
    setPurgeReport(null);
    try {
      const r = await axios.get(`${API}/master/purge-email/check`, {
        headers: authH(), params: { email: em },
      });
      setCheckReport(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Check failed');
    } finally {
      setChecking(false);
    }
  };

  const runPurge = async () => {
    const em = email.trim();
    if (!em) return;
    setPurging(true);
    try {
      const r = await axios.post(`${API}/master/purge-email`, { email: em }, { headers: authH() });
      setPurgeReport(r.data);
      toast.success(`Email ${em} fully purged.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Purge failed');
    } finally {
      setPurging(false);
    }
  };

  return (
    <>
      <Button
        onClick={() => { reset(); setOpen(true); }}
        variant="outline"
        className="bg-white/5 border-white/10 text-white text-sm rounded-xl gap-2 hover:bg-rose-500/10 hover:border-rose-500/40 hover:text-rose-300"
        data-testid="open-purge-email"
      >
        <Trash2 size={14} /> Purge Email
      </Button>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
        <DialogContent className="max-w-lg bg-[#171A2C] border border-white/10" data-testid="purge-email-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Mail size={16} className="text-[#A78BFA]" /> Purge & Unblock Email
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-[13px] text-white/70">
              Hard-wipes an email from every MongoDB collection so it becomes fully re-registerable.
              Useful when a deleted client-staging email is still blocking new signup. <span className="text-rose-300">This action cannot be undone.</span>
            </p>

            <div className="flex gap-2">
              <Input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="client-staging@example.com"
                type="email"
                className="flex-1 bg-white/5 border-white/10 text-white"
                data-testid="purge-email-input"
              />
              <Button
                onClick={runCheck}
                disabled={checking || !email}
                className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white gap-2"
                data-testid="purge-email-check"
              >
                {checking ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                Check
              </Button>
            </div>

            {/* Diagnostic report */}
            {checkReport && (
              <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-3" data-testid="purge-check-report">
                <div className="flex items-center gap-2">
                  {checkReport.is_blocked_for_signup ? (
                    <><AlertTriangle size={14} className="text-amber-300" />
                    <p className="text-amber-300 text-sm font-bold">This email cannot register right now</p></>
                  ) : Object.keys(checkReport.found || {}).length === 0 ? (
                    <><Check size={14} className="text-emerald-300" />
                    <p className="text-emerald-300 text-sm font-bold">Nothing found — email is already clear</p></>
                  ) : (
                    <><AlertTriangle size={14} className="text-white/60" />
                    <p className="text-white text-sm font-bold">Traces found (may or may not block signup)</p></>
                  )}
                </div>
                {Object.keys(checkReport.found || {}).length > 0 && (
                  <ul className="space-y-1.5 text-[12px]">
                    {Object.entries(checkReport.found).map(([coll, meta]) => (
                      <li key={coll} className="flex items-start gap-2 text-white/70">
                        <span className="text-white/40 shrink-0">•</span>
                        <div>
                          <span className="font-mono text-[#A78BFA]">{coll}</span>
                          <span className="text-white/50 ml-2">{JSON.stringify(meta)}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                {Object.keys(checkReport.found || {}).length > 0 && !purgeReport && (
                  <Button
                    onClick={runPurge}
                    disabled={purging}
                    className="w-full bg-rose-500 hover:bg-rose-600 text-white gap-2"
                    data-testid="purge-email-confirm"
                  >
                    {purging ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                    Purge {checkReport.email} everywhere
                  </Button>
                )}
              </div>
            )}

            {/* Purge summary */}
            {purgeReport && (
              <div className="rounded-xl border border-emerald-400/25 bg-emerald-500/10 p-4" data-testid="purge-report">
                <div className="flex items-center gap-2 mb-2">
                  <Check size={14} className="text-emerald-300" />
                  <p className="text-emerald-300 text-sm font-bold">Purged. Email is now available.</p>
                </div>
                {Object.keys(purgeReport.deleted || {}).length > 0 ? (
                  <ul className="space-y-1 text-[11.5px] text-white/70">
                    {Object.entries(purgeReport.deleted).map(([coll, n]) => (
                      <li key={coll}>
                        <span className="font-mono text-emerald-300">{coll}</span> — {n} record{n !== 1 ? 's' : ''} deleted
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-[11.5px] text-white/60">No residual records to remove — the email was already clean.</p>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
